import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, RefreshCw, ArrowLeft, Users } from "lucide-react";
import { toast } from "sonner";
import { fetchCommunitySubmissions, updateCommunitySubmission, mediaUrl } from "@/lib/api";

const STATUSES = ["new", "reviewing", "featured", "archived"];
const STATUS_COLOR = {
  new: "text-signal",
  reviewing: "text-yellow-400",
  featured: "text-rankup",
  archived: "text-muted-foreground",
};

export default function AdminCommunity() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetchCommunitySubmissions().then(setSubs).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateCommunitySubmission(id, status);
      setSubs((s) => s.map((x) => (x.id === id ? updated : x)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const counts = STATUSES.reduce((acc, s) => {
    acc[s] = subs.filter((x) => x.status === s).length;
    return acc;
  }, {});

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
      <Link to="/admin/leads" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-signal transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to admin
      </Link>
      <div className="flex items-end justify-between gap-4 border-b border-white/10 pb-8 mt-4">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Internal</span>
          <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight mt-3 leading-none" data-testid="admin-community-title">
            Community Inbox
          </h1>
        </div>
        <button data-testid="community-refresh" onClick={load} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 px-4 py-2 hover:border-signal hover:text-signal transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border-x border-b border-white/10 mt-8">
        <div className="bg-background p-5">
          <div className="font-mono-stat text-3xl">{subs.length}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Total</div>
        </div>
        {STATUSES.map((s) => (
          <div key={s} className="bg-background p-5">
            <div className={`font-mono-stat text-3xl ${STATUS_COLOR[s]}`}>{counts[s] || 0}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{s}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="font-mono text-sm text-muted-foreground mt-10">Loading…</p>
      ) : subs.length === 0 ? (
        <div className="border border-white/10 bg-card p-10 text-center mt-10" data-testid="admin-community-empty">
          <Users className="w-8 h-8 text-muted-foreground mx-auto" />
          <p className="font-mono text-sm text-muted-foreground mt-3">No submissions yet. They land here from the Community page.</p>
        </div>
      ) : (
        <div className="border-x border-b border-white/10 divide-y divide-white/10 mt-10">
          {subs.map((s) => (
            <div key={s.id} data-testid={`community-row-${s.id}`} className="p-5 md:px-6 grid md:grid-cols-[auto_1fr_auto] gap-4 items-start hover:bg-secondary transition-colors">
              {s.image ? (
                <a href={mediaUrl(s.image)} target="_blank" rel="noopener noreferrer" className="block w-24 h-24 shrink-0 overflow-hidden border border-white/15">
                  <img src={mediaUrl(s.image)} alt={s.name} className="w-full h-full object-cover" />
                </a>
              ) : (
                <div className="w-24 h-24 shrink-0 border border-white/10 bg-background flex items-center justify-center">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground/50">No photo</span>
                </div>
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-display text-xl font-medium">{s.name}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-widest ${STATUS_COLOR[s.status]}`}>● {s.status}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest bg-secondary px-2 py-0.5">{s.submission_type}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{s.category}</span>
                </div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mt-1">
                  <a href={`mailto:${s.email}`} className="text-signal hover:underline inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</a>
                  {s.handle ? ` · ${s.handle}` : ""} · {new Date(s.created_at).toLocaleDateString()}
                </div>
                <p className="text-foreground/80 text-sm mt-2 max-w-3xl whitespace-pre-wrap">{s.message}</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map((st) => (
                  <button
                    key={st}
                    data-testid={`community-status-${s.id}-${st}`}
                    onClick={() => changeStatus(s.id, st)}
                    className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${
                      s.status === st ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/60 hover:border-signal"
                    }`}
                  >
                    {st}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
